import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

interface BracketMatch {
  id: number;
  player1: string;
  player2: string;
  date: string;
  time: string;
  court: string;
  courtHidden?: boolean; // organizer hid the pre-assigned court from participants
  result?: string;
  winner?: string;
  sets?: {
    player1: number[];
    player2: number[];
  };
  sourceGroup?: string;
}

interface BracketData {
  octavos: BracketMatch[];
  cuartos: BracketMatch[];
  semifinales: BracketMatch[];
  final: BracketMatch[];
}

interface BracketTreeViewProps {
  onBack?: () => void;
  bracketData: BracketData;
  sport: string;
  gameType: 'singles' | 'doubles';
  userGroup?: string;
  showBackButton?: boolean;
  modality?: 'round-robin' | 'elimination' | 'sum-games';
}

const BracketTreeView = ({ onBack, bracketData, sport, gameType, userGroup, showBackButton = true, modality }: BracketTreeViewProps) => {
  // Función para formatear nombres según el tipo de juego
  const formatPlayerName = (playerName: string) => {
    if (gameType === 'singles') {
      // En singles, si viene con formato "Apellido/Apellido", tomar solo el primero
      // o si viene con formato completo, extraer solo un jugador
      if (playerName.includes('/')) {
        return playerName.split('/')[0];
      }
      return playerName;
    }
    // En doubles, mantener el formato completo
    return playerName;
  };

  const renderMatch = (match: BracketMatch, isSmall = false) => {
    return (
      <div key={match.id} className="space-y-2">
        {/* Recuadro principal del partido */}
        <div className={`border rounded-lg bg-white shadow-sm ${isSmall ? 'p-2' : 'p-3'}`}>
          <div className="space-y-2">
            {/* Jugador 1 */}
            <div className="flex items-center">
              <span className={`${isSmall ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                {formatPlayerName(match.player1)}
              </span>
            </div>
            
            {/* Jugador 2 */}
            <div className="flex items-center">
              <span className={`${isSmall ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                {formatPlayerName(match.player2)}
              </span>
            </div>
            
            {/* Información de fecha, hora y cancha dentro del recuadro */}
            <div className="flex items-center justify-between pt-1 border-t border-gray-100">
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <span>📅 {match.date}</span>
                <span>🕐 {match.time}</span>
                {/* Hide the pre-assigned court when the organizer hid courts */}
                {!match.courtHidden && match.court && <span>🎾 C{match.court}</span>}
              </div>
              
              {/* Estado del partido */}
              {match.result ? (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-800 px-1 py-0 scale-75">
                  Finalizado
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-700 px-1 py-0 scale-75">
                  Programado
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Determinar el texto del botón según la modalidad
  const getBackButtonText = () => {
    if (modality === 'elimination') {
      return 'Volver a Mi Partido';
    }
    return 'Volver a Mi Grupo';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <span className="text-base md:text-lg font-bold text-gray-900">
              Brackets Programados - Fase de Eliminación
            </span>
            {showBackButton && onBack && (
              <Button 
                onClick={onBack}
                variant="outline"
                size="sm"
                className="text-[#3D6B4F] border-emerald-300 hover:bg-emerald-50 text-xs px-2 py-1 h-auto"
              >
                <ArrowLeft className="mr-1 h-3 w-3" />
                {getBackButtonText()}
              </Button>
            )}
          </CardTitle>
          {modality !== 'elimination' && (
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800 font-medium">
                ℹ️ Estos son los horarios programados para la fase de eliminación. Los participantes se definirán una vez termine la fase de grupos.
              </p>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Bracket Tree Visualization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-center text-lg md:text-xl font-bold text-gray-900">
            Árbol del Torneo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            
            {/* Octavos de Final */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-center text-gray-900 mb-4">Octavos de Final</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bracketData.octavos.map(match => renderMatch(match, true))}
              </div>
            </div>

            {/* Cuartos de Final */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-center text-gray-900 mb-4">Cuartos de Final</h3>
              <div className="grid grid-cols-1 gap-4">
                {bracketData.cuartos.map(match => renderMatch(match))}
              </div>
            </div>

            {/* Semifinales */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-center text-gray-900 mb-4">Semifinales</h3>
              <div className="grid grid-cols-1 gap-4">
                {bracketData.semifinales.map(match => renderMatch(match))}
              </div>
            </div>

            {/* Final */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-center text-gray-900 mb-4">Final</h3>
              <div className="flex flex-col items-center space-y-6">
                <div className="w-full max-w-md">
                  {bracketData.final.map(match => renderMatch(match))}
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-700 mt-2">Campeón</p>
                </div>
              </div>
            </div>

          </div>

          {/* Líneas conectoras (decorativas) */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
              <div className="w-4 h-px bg-gray-300"></div>
              <span>Los ganadores avanzan a la siguiente ronda</span>
              <div className="w-4 h-px bg-gray-300"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BracketTreeView;